import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import {
  Play,
  GitBranch,
  CalendarDays,
  Clock,
  Bot,
  UserPlus,
  X,
  ThumbsDown,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Calendar,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useUpdateItem, useDismissItem, useDismissNotImportant } from "@/hooks/useItems";
import { useSubtasks } from "@/hooks/useSubtasks";
import { useFocus } from "@/contexts/FocusContext";
import { BreakDownView } from "./BreakDownView";
import { ScheduleView } from "./ScheduleView";
import { SnoozeView } from "./SnoozeView";
import { ItemEditSheet } from "./ItemEditSheet";
import { DispatchSheet } from "@/components/agent/DispatchSheet";
import { DelegateSheet } from "./DelegateSheet";
import { ItemChat } from "./ItemChat";
import { AgentTimeline } from "@/components/agent/AgentTimeline";
import { toast } from "sonner";
import type { Item } from "@/lib/types";

const priorityColors = {
  high: "bg-accent",
  medium: "bg-secondary",
  low: "bg-muted-foreground/50",
};

type SubView = null | "breakdown" | "schedule" | "snooze";

type Props = {
  item: Item | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ItemActionSheet({ item, open, onOpenChange }: Props) {
  const [subView, setSubView] = useState<SubView>(null);
  const [showChat, setShowChat] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [showDelegate, setShowDelegate] = useState(false);
  const updateItem = useUpdateItem();
  const dismissItem = useDismissItem();
  const dismissNotImportant = useDismissNotImportant();
  const focus = useFocus();
  const { data: subtasks } = useSubtasks(item?.id ?? null);

  // Reset sub-view when sheet opens/closes.
  // If a sub-view is active, swipe/close goes BACK to the tile grid instead of
  // closing the whole sheet — so the user stays in the task.
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (subView) {
        setSubView(null);
        return; // block full close — go back to tile grid
      }
      setShowChat(false);
      setShowEdit(false);
      setShowDelegate(false);
    }
    onOpenChange(open);
  };

  if (!item) return null;

  // If editing, show the edit sheet instead
  if (showEdit) {
    return (
      <ItemEditSheet
        item={item}
        open={open}
        onOpenChange={(o) => {
          if (!o) setShowEdit(false);
          handleOpenChange(o);
        }}
      />
    );
  }

  // If dispatching, show dispatch sheet — swipe/close goes back, not fully out
  if (showDispatch) {
    return (
      <DispatchSheet
        item={item}
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setShowDispatch(false);
            // Don't propagate — stay in the task, return to tile grid
          } else {
            onOpenChange(o);
          }
        }}
      />
    );
  }

  // If delegating, show delegate sheet — swipe/close goes back, not fully out
  if (showDelegate) {
    return (
      <DelegateSheet
        item={item}
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setShowDelegate(false);
            // Don't propagate — stay in the task, return to tile grid
          } else {
            onOpenChange(o);
          }
        }}
      />
    );
  }

  const age = formatDistanceToNow(new Date(item.created_at), { addSuffix: false });

  const handleDoItNow = () => {
    updateItem.mutate(
      { id: item.id, status: "active" },
      {
        onSuccess: () => {
          focus.startFocus(item);
          handleOpenChange(false);
        },
      }
    );
  };

  const handleKill = () => {
    dismissItem.mutate(item.id, {
      onSuccess: () => {
        toast("Item dismissed");
        handleOpenChange(false);
      },
    });
  };

  const closeSub = () => {
    setSubView(null);
  };

  const doneAndClose = () => {
    setSubView(null);
    handleOpenChange(false);
  };

  const doneSubtasks = subtasks?.filter((s) => s.status === "done").length ?? 0;
  const totalSubtasks = subtasks?.length ?? 0;

  const tiles = [
    { key: "doit", label: "Do It Now", icon: Play, color: "text-green-500", bg: "bg-green-500/10 hover:bg-green-500/20 border-green-500/20" },
    { key: "breakdown", label: "Break It Down", icon: GitBranch, color: "text-purple-500", bg: "bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/20" },
    { key: "schedule", label: "Schedule It", icon: CalendarDays, color: "text-blue-500", bg: "bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20" },
    { key: "snooze", label: "Snooze", icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20" },
    { key: "dispatch", label: "Dispatch", icon: Bot, color: "text-cyan-500", bg: "bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/20" },
    { key: "delegate", label: "Delegate", icon: UserPlus, color: "text-indigo-500", bg: "bg-indigo-500/10 hover:bg-indigo-500/20 border-indigo-500/20" },
    { key: "not_important", label: "Not Important", icon: ThumbsDown, color: "text-amber-500", bg: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20" },
    { key: "kill", label: "Kill It", icon: X, color: "text-red-400", bg: "bg-red-500/10 hover:bg-red-500/20 border-red-500/20" },
  ];

  const handleTileClick = (key: string) => {
    switch (key) {
      case "doit":
        handleDoItNow();
        break;
      case "breakdown":
        setSubView("breakdown");
        break;
      case "schedule":
        setSubView("schedule");
        break;
      case "snooze":
        setSubView("snooze");
        break;
      case "dispatch":
        setShowDispatch(true);
        break;
      case "delegate":
        setShowDelegate(true);
        break;
      case "not_important":
        dismissNotImportant.mutate(item.id, {
          onSuccess: () => {
            toast("Noted — will learn to skip items like this");
            handleOpenChange(false);
          },
        });
        break;
      case "kill":
        handleKill();
        break;
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:w-3/4 sm:max-w-md flex flex-col h-full overflow-y-auto p-0">
        <div className={cn("px-6 pt-6 pb-2 shrink-0", showChat && "pb-0")}>
          <SheetHeader>
            <SheetTitle className="text-left">{item.title}</SheetTitle>
            <SheetDescription className="sr-only">
              Choose an action for this item.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-3 mt-3">
            {/* Header meta */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-2.5 h-2.5 rounded-full ${priorityColors[item.priority]}`} />
              <span className="text-xs text-muted-foreground capitalize">{item.priority}</span>
              {item.project_tag && (
                <Badge variant="secondary" className="text-xs">
                  {item.project_tag}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">{age} old</span>
              {totalSubtasks > 0 && (
                <Badge variant="outline" className="text-xs">
                  {doneSubtasks}/{totalSubtasks} subtasks
                </Badge>
              )}
            </div>

            {/* Context: raw_input, date_target, notes */}
            {item.raw_input && item.raw_input !== item.title && (
              <p className="text-xs text-muted-foreground italic">
                {item.raw_input}
              </p>
            )}
            {item.date_target && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {new Date(item.date_target + "T00:00:00").toLocaleDateString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}
            {item.notes && (
              <div className="text-xs text-muted-foreground whitespace-pre-line bg-muted/50 rounded-lg px-3 py-2 max-h-32 overflow-y-auto">
                {item.notes}
              </div>
            )}

            {!showChat && (
              <>
                {/* Sub-view or tile grid */}
                {subView === "breakdown" ? (
                  <div className="space-y-2">
                    <button
                      onClick={closeSub}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="h-3 w-3" /> Back to actions
                    </button>
                    <BreakDownView item={item} onDone={doneAndClose} />
                  </div>
                ) : subView === "schedule" ? (
                  <div className="space-y-2">
                    <button
                      onClick={closeSub}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="h-3 w-3" /> Back to actions
                    </button>
                    <ScheduleView item={item} onDone={doneAndClose} />
                  </div>
                ) : subView === "snooze" ? (
                  <div className="space-y-2">
                    <button
                      onClick={closeSub}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="h-3 w-3" /> Back to actions
                    </button>
                    <SnoozeView item={item} onDone={doneAndClose} />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {tiles.map(({ key, label, icon: Icon, color, bg }) => (
                      <button
                        key={key}
                        onClick={() => handleTileClick(key)}
                        className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border transition-colors ${bg}`}
                      >
                        <Icon className={`h-5 w-5 ${color}`} />
                        <span className="text-xs font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Subtasks list if any */}
                {totalSubtasks > 0 && !subView && (
                  <div className="space-y-1.5">
                    <h4 className="text-xs font-medium text-muted-foreground">
                      Subtasks ({doneSubtasks}/{totalSubtasks})
                    </h4>
                    <div className="space-y-1">
                      {subtasks?.map((sub) => (
                        <div
                          key={sub.id}
                          className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded ${
                            sub.status === "done" ? "text-muted-foreground line-through" : ""
                          }`}
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              sub.status === "done" ? "bg-green-500" : "bg-muted-foreground/50"
                            }`}
                          />
                          {sub.title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!subView && (
                  <AgentTimeline itemId={item.id} />
                )}

                {/* Edit details link */}
                {!subView && (
                  <button
                    onClick={() => setShowEdit(true)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ChevronDown className="h-3 w-3" /> Edit details
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Contextual AI chat — collapsible */}
        {!subView && (
          showChat ? (
            <div className="flex-1 min-h-0 flex flex-col">
              <button
                onClick={() => setShowChat(false)}
                className="shrink-0 flex items-center justify-between px-6 py-2 border-t border-border hover:bg-muted/50 transition-colors"
              >
                <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <MessageSquare className="h-3.5 w-3.5" /> AI Chat
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              <div className="flex-1 min-h-0">
                <ItemChat item={item} />
              </div>
            </div>
          ) : (
            <div className="shrink-0 mt-auto">
              <button
                onClick={() => setShowChat(true)}
                className="w-full flex items-center justify-between px-6 py-3 border-t border-border hover:bg-muted/50 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MessageSquare className="h-4 w-4" /> AI Chat
                </span>
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          )
        )}
      </SheetContent>
    </Sheet>
  );
}
